import { useGetLocale, useTranslate } from "@refinedev/core";
import { Button, type Column } from "@fawkes/ui";

import { formatDateTime } from "../i18n/format";
import { isInternalAddress } from "./ipAddressKind";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Pairing epic D (D8): the recent refused token requests of a service account — why the token endpoint
// said no (reason) and from which address (the caller IP as resolved behind the trusted proxies). The
// wire answer to the caller is always the uniform invalid_client; this is the admin's side of it. Used on
// the service-account detail (allowlist tab) and in the platform pairing screen.
//
// An internal address (loopback, private, link-local) is never offered for the allowlist: it is this
// backoffice's own reverse proxy, which fawkes.api.service-account-token.trusted-proxies does not trust yet, and
// allowing it would let every caller through the proxy pass (ACC 2026-09-14: 0:0:0:0:0:0:0:1).

export interface TokenRejection {
  occurredAt: string;
  reason: string;
  clientIp: string | null;
  clientIdPrefix: string | null;
}

const KNOWN_REASONS = [
  "ip_mismatch",
  "bad_secret",
  "removed_credential",
  "expired_credential",
  "disabled_account",
  "no_authorities",
];

export function TokenRejectionList({
  rejections,
  onAllowIp,
  allowLabel,
  disabled,
  empty,
}: {
  rejections: TokenRejection[];
  /** Offered on an ip_mismatch row from a public address: allow that caller IP. */
  onAllowIp?: (ip: string) => void;
  allowLabel?: (ip: string) => string;
  disabled?: boolean;
  empty: string;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "en";

  const refusedFromProxy = (r: TokenRejection) => r.reason === "ip_mismatch" && isInternalAddress(r.clientIp);

  const columns: Column<TokenRejection>[] = [
    { header: t("tokenRejection.column.time"), cell: (r) => formatDateTime(r.occurredAt, locale) },
    {
      header: t("tokenRejection.column.reason"),
      cell: (r) => (KNOWN_REASONS.includes(r.reason) ? t(`tokenRejection.reason.${r.reason}`) : r.reason),
    },
    { header: t("tokenRejection.column.ip"), cell: (r) => r.clientIp ?? "—" },
    {
      header: "",
      cell: (r) => {
        if (refusedFromProxy(r)) {
          return <span className="fawkes-muted">{t("tokenRejection.proxyAddress")}</span>;
        }
        return onAllowIp && r.clientIp && r.reason === "ip_mismatch" ? (
          <Button variant="secondary" onClick={() => onAllowIp(r.clientIp!)} disabled={disabled}>
            {allowLabel ? allowLabel(r.clientIp) : t("tokenRejection.allowIp")}
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <>
      <IndeelbareTabel tabelKey="token-rejection-list"
        columns={columns}
        rows={rejections}
        getRowKey={(r) => `${r.occurredAt}-${r.reason}-${r.clientIp ?? ""}`}
        empty={empty}
      />
      {rejections.some(refusedFromProxy) && (
        <p className="fawkes-error" role="note">
          {t("tokenRejection.proxyAddress.help")}
        </p>
      )}
    </>
  );
}
